"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatabaseImportLimitCheck = exports.DATABASE_ROW_COUNTER_SHARE = exports.DATABASE_SYSTEM_LIMITS = exports.DATABASE_PRODUCT_LIMITS = exports.DatabaseLimitError = exports.DATABASE_LIMIT_EXCEEDED_CODE = void 0;
exports.decodeDatabaseLimitMessage = decodeDatabaseLimitMessage;
exports.databaseLimitMessageKey = databaseLimitMessageKey;
exports.isDatabaseLimitError = isDatabaseLimitError;
exports.measureDatabaseCellValue = measureDatabaseCellValue;
exports.assertDatabaseCellWithinTypeLimit = assertDatabaseCellWithinTypeLimit;
exports.estimateDatabaseCellValueBytes = estimateDatabaseCellValueBytes;
exports.estimateDatabaseRowBytes = estimateDatabaseRowBytes;
exports.assertDatabaseRowWithinByteBudget = assertDatabaseRowWithinByteBudget;
exports.databaseRowCounter = databaseRowCounter;
exports.assertDatabaseRowCountWithinLimit = assertDatabaseRowCountWithinLimit;
exports.databaseColumnOptionCount = databaseColumnOptionCount;
exports.databaseColumnOptionsWithinLimit = databaseColumnOptionsWithinLimit;
exports.databaseImportedColumnOptions = databaseImportedColumnOptions;
exports.assertDatabaseColumnOptionsWithinLimit = assertDatabaseColumnOptionsWithinLimit;
exports.assertDatabaseImportWithinLimits = assertDatabaseImportWithinLimits;
const database_large_data_policy_1 = require("./database-large-data-policy");
exports.DATABASE_LIMIT_EXCEEDED_CODE = 'DATABASE_LIMIT_EXCEEDED';
/**
 * The one error every database limit check throws. UI, GraphQL, MCP and
 * automation map it by `code` and read `detail`; the message is technical.
 */
class DatabaseLimitError extends Error {
    constructor(detail, source) {
        super(`${exports.DATABASE_LIMIT_EXCEEDED_CODE}: ${source} exceeds the ${detail.kind} limit of ${detail.limit} ${detail.unit} (actual ${detail.actual})`);
        this.detail = detail;
        this.source = source;
        this.code = exports.DATABASE_LIMIT_EXCEEDED_CODE;
        this.name = 'DatabaseLimitError';
    }
}
exports.DatabaseLimitError = DatabaseLimitError;
const DATABASE_LIMIT_MESSAGE_PATTERN = new RegExp(`${exports.DATABASE_LIMIT_EXCEEDED_CODE}: .*? exceeds the (rows|columns|cell|column-options|row-size|import) limit of (\\d+) (rows|columns|chars|links|options|members|files|bytes) \\(actual (\\d+)\\)`);
/**
 * Reads the limit back from an error message, for consumers that only
 * receive text (automation row errors, execution logs).
 */
function decodeDatabaseLimitMessage(message) {
    const match = DATABASE_LIMIT_MESSAGE_PATTERN.exec(message);
    if (!match)
        return null;
    return {
        kind: match[1],
        limit: Number(match[2]),
        unit: match[3],
        actual: Number(match[4]),
    };
}
/** i18n key suffix of `database.limit.*`; byte limits map to `rowSize`. */
function databaseLimitMessageKey(detail) {
    switch (detail.kind) {
        case 'rows':
            return 'rows';
        case 'columns':
            return 'columns';
        case 'column-options':
            return 'columnOptions';
        case 'row-size':
            return 'rowSize';
        case 'cell':
        case 'import':
            switch (detail.unit) {
                case 'rows':
                    return 'rows';
                case 'columns':
                    return 'columns';
                case 'chars':
                case 'links':
                case 'options':
                case 'members':
                case 'files':
                    return `cell.${detail.unit}`;
                default:
                    return 'rowSize';
            }
    }
}
function isDatabaseLimitError(error) {
    return (error instanceof DatabaseLimitError ||
        (error instanceof Error &&
            error.code === exports.DATABASE_LIMIT_EXCEEDED_CODE &&
            typeof error.detail === 'object'));
}
/** Limits of every user and API action. */
exports.DATABASE_PRODUCT_LIMITS = {
    rowHardLimit: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.database.rowHardLimit,
    columnHardLimit: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.database.columnHardLimit,
    columnOptionHardLimit: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.column.optionHardLimit,
    rowEncodedByteHardLimit: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.row.encodedByteHardLimit,
    cellLimits: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.cell.limits,
};
/** Limits DocPersist admission enforces on every persisted database. */
exports.DATABASE_SYSTEM_LIMITS = {
    ...exports.DATABASE_PRODUCT_LIMITS,
    rowHardLimit: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.database.systemRowHardLimit,
    columnHardLimit: database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.database.systemColumnHardLimit,
};
function isTextLike(value) {
    return (typeof value.length === 'number' &&
        typeof value.toDelta === 'function');
}
function countChars(value, source) {
    if (value === null || value === undefined)
        return 0;
    if (typeof value === 'string')
        return value.length;
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value).length;
    }
    if (Array.isArray(value)) {
        // Rich-text delta: [{ insert: string, attributes? }].
        return value.reduce((sum, op) => {
            const insert = op?.insert;
            return sum + (typeof insert === 'string' ? insert.length : 0);
        }, 0);
    }
    if (typeof value === 'object') {
        if (isTextLike(value))
            return value.length;
        // Serialized rich text of imports and snapshots: { $blocksuite:internal:text$, delta }.
        if ('$blocksuite:internal:text$' in value) {
            return countChars(value.delta ?? [], source);
        }
        const yText = value.yText;
        if (yText && typeof yText === 'object' && isTextLike(yText)) {
            return yText.length;
        }
    }
    throw new TypeError(`${source} has an unsupported text cell value`);
}
function countItems(value, source) {
    if (value === null || value === undefined)
        return 0;
    if (Array.isArray(value))
        return value.length;
    if (typeof value === 'object') {
        const asJson = value.toJSON;
        const plain = typeof asJson === 'function' ? asJson.call(value) : value;
        if (Array.isArray(plain))
            return plain.length;
        if (plain && typeof plain === 'object')
            return Object.keys(plain).length;
    }
    throw new TypeError(`${source} has an unsupported list cell value`);
}
/** Size of a cell value in the unit of its column type's limit. */
function measureDatabaseCellValue(limit, value, source) {
    return limit.unit === 'chars'
        ? countChars(value, source)
        : countItems(value, source);
}
function assertDatabaseCellWithinTypeLimit({ source, columnType, value, columnId, rowId }, policy = exports.DATABASE_PRODUCT_LIMITS) {
    const limit = policy.cellLimits[columnType];
    if (!limit)
        return;
    const actual = measureDatabaseCellValue(limit, value, source);
    if (actual <= limit.limit)
        return;
    throw new DatabaseLimitError({
        kind: 'cell',
        limit: limit.limit,
        actual,
        unit: limit.unit,
        columnType,
        ...(columnId === undefined ? {} : { columnId }),
        ...(rowId === undefined ? {} : { rowId }),
    }, source);
}
const databaseValueTextEncoder = new TextEncoder();
function estimateDatabaseCellValueBytes(value) {
    if (typeof value === 'string') {
        return databaseValueTextEncoder.encode(value).byteLength;
    }
    if (value instanceof Uint8Array) {
        return value.byteLength;
    }
    return databaseValueTextEncoder.encode(JSON.stringify(value ?? null))
        .byteLength;
}
/** Encoded size estimate of a row; the row-shard writers budget with it. */
function estimateDatabaseRowBytes(cells) {
    let bytes = 0;
    for (const [columnId, value] of cells) {
        bytes +=
            databaseValueTextEncoder.encode(columnId).byteLength +
                estimateDatabaseCellValueBytes(value);
    }
    return bytes;
}
function assertDatabaseRowWithinByteBudget({ source, cells, rowId }, policy = exports.DATABASE_PRODUCT_LIMITS) {
    const actual = estimateDatabaseRowBytes(cells);
    if (actual <= policy.rowEncodedByteHardLimit)
        return;
    throw new DatabaseLimitError({
        kind: 'row-size',
        limit: policy.rowEncodedByteHardLimit,
        actual,
        unit: 'bytes',
        ...(rowId === undefined ? {} : { rowId }),
    }, source);
}
/** Share of the row limit from which a database shows its row counter. */
exports.DATABASE_ROW_COUNTER_SHARE = database_large_data_policy_1.DATABASE_LARGE_DATA_POLICY.database.rowCounterShare;
/**
 * The visible row counter, or `null` below the counter share of the limit.
 * `overLimit` marks a database that offline merges pushed above the limit.
 */
function databaseRowCounter(rowCount, policy = exports.DATABASE_PRODUCT_LIMITS) {
    const limit = policy.rowHardLimit;
    return rowCount >= limit * exports.DATABASE_ROW_COUNTER_SHARE
        ? { count: rowCount, limit, overLimit: rowCount > limit }
        : null;
}
/**
 * Only growth is limited: a database above the limit (after an offline merge)
 * stays editable, it just accepts no further rows.
 */
function assertDatabaseRowCountWithinLimit({ source, current, adding }, policy = exports.DATABASE_PRODUCT_LIMITS) {
    if (adding <= 0)
        return;
    const actual = current + adding;
    if (actual <= policy.rowHardLimit)
        return;
    throw new DatabaseLimitError({ kind: 'rows', limit: policy.rowHardLimit, actual, unit: 'rows' }, source);
}
const OPTION_COLUMN_TYPES = new Set([
    'select',
    'multi-select',
]);
/** The number of options a column definition offers; 0 for other data. */
function databaseColumnOptionCount(data) {
    if (!data || typeof data !== 'object')
        return 0;
    const options = data.options;
    return Array.isArray(options) ? options.length : 0;
}
/**
 * Whether a column may go from `current` to `next` options. Only growth
 * beyond the limit is refused: a column an offline merge lifted above it
 * stays editable.
 */
function databaseColumnOptionsWithinLimit(current, next, policy = exports.DATABASE_PRODUCT_LIMITS) {
    return next <= current || next <= policy.columnOptionHardLimit;
}
/**
 * The options an imported column keeps: the first `columnOptionHardLimit`
 * in the order the source names them. An import names every column it cuts
 * and writes only after the user confirms.
 */
function databaseImportedColumnOptions(options, policy = exports.DATABASE_PRODUCT_LIMITS) {
    return options.length > policy.columnOptionHardLimit
        ? options.slice(0, policy.columnOptionHardLimit)
        : options;
}
/**
 * Only growth is limited: a select column above the limit (after an offline
 * merge) stays editable, it just accepts no further options.
 */
function assertDatabaseColumnOptionsWithinLimit({ source, columnType, columnId, current, next, }, policy = exports.DATABASE_PRODUCT_LIMITS) {
    if (!OPTION_COLUMN_TYPES.has(columnType))
        return;
    if (databaseColumnOptionsWithinLimit(current, next, policy))
        return;
    throw new DatabaseLimitError({
        kind: 'column-options',
        limit: policy.columnOptionHardLimit,
        actual: next,
        unit: 'options',
        columnType,
        ...(columnId ? { columnId } : {}),
    }, source);
}
/**
 * Import preflight that streams over the source rows before the first write.
 * The first violation throws a `DatabaseLimitError` of kind `import`;
 * `rowIndex` is the 1-based data row of the source (header excluded) and
 * `columnId` names the cell's column.
 */
class DatabaseImportLimitCheck {
    constructor({ source, existingRows = 0, columns }, policy = exports.DATABASE_PRODUCT_LIMITS) {
        this.policy = policy;
        this.rowIndex = 0;
        this.source = source;
        this.existingRows = existingRows;
        this.columnTypes = new Map(columns.map(column => [column.id, column.type]));
        if (columns.length > policy.columnHardLimit) {
            this.fail({
                limit: policy.columnHardLimit,
                actual: columns.length,
                unit: 'columns',
            });
        }
    }
    /** Checks the next source row; `cells` are keyed by column id. */
    row(cells) {
        const rowIndex = ++this.rowIndex;
        const rows = this.existingRows + rowIndex;
        if (rows > this.policy.rowHardLimit) {
            this.fail({
                limit: this.policy.rowHardLimit,
                actual: rows,
                unit: 'rows',
                rowIndex,
            });
        }
        const rowCells = [];
        for (const [columnId, value] of cells) {
            const columnType = this.columnTypes.get(columnId);
            const limit = columnType && this.policy.cellLimits[columnType];
            if (columnType && limit) {
                const actual = measureDatabaseCellValue(limit, value, this.source);
                if (actual > limit.limit) {
                    this.fail({
                        limit: limit.limit,
                        actual,
                        unit: limit.unit,
                        columnType,
                        columnId,
                        rowIndex,
                    });
                }
            }
            rowCells.push([columnId, value]);
        }
        const bytes = estimateDatabaseRowBytes(rowCells);
        if (bytes > this.policy.rowEncodedByteHardLimit) {
            this.fail({
                limit: this.policy.rowEncodedByteHardLimit,
                actual: bytes,
                unit: 'bytes',
                rowIndex,
            });
        }
    }
    fail(detail) {
        throw new DatabaseLimitError({ kind: 'import', ...detail }, this.source);
    }
}
exports.DatabaseImportLimitCheck = DatabaseImportLimitCheck;
function assertDatabaseImportWithinLimits(input, policy = exports.DATABASE_PRODUCT_LIMITS) {
    const check = new DatabaseImportLimitCheck(input, policy);
    for (const cells of input.rows)
        check.row(cells);
}
